import { createContext, useContext, useState, useCallback, useMemo } from "react";
import Joyride, { STATUS, EVENTS, ACTIONS } from "react-joyride";
import { useAuth } from "./AuthContext";

const TourContext = createContext(null);

const STORAGE_PREFIX = "stacked.tours.";
const SEEN_KEY = (userId, pageKey) => `${STORAGE_PREFIX}seen.${userId || "anon"}.${pageKey}`;
const SKIP_ALL_KEY = (userId) => `${STORAGE_PREFIX}skipAll.${userId || "anon"}`;

function readSeen(userId, pageKey) {
  try { return localStorage.getItem(SEEN_KEY(userId, pageKey)) === "1"; } catch { return false; }
}
function writeSeen(userId, pageKey) {
  try { localStorage.setItem(SEEN_KEY(userId, pageKey), "1"); } catch {}
}
function readSkipAll(userId) {
  try { return localStorage.getItem(SKIP_ALL_KEY(userId)) === "1"; } catch { return false; }
}
function writeSkipAll(userId, value) {
  try {
    if (value) localStorage.setItem(SKIP_ALL_KEY(userId), "1");
    else localStorage.removeItem(SKIP_ALL_KEY(userId));
  } catch {}
}

export function TourProvider({ children }) {
  const { user } = useAuth() || {};
  const userId = user?._id || user?.id || null;

  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [pageKey, setPageKey] = useState(null);

  const startTour = useCallback((nextPageKey, nextSteps, { force = false } = {}) => {
    if (!nextPageKey || !Array.isArray(nextSteps) || nextSteps.length === 0) return;
    if (!force) {
      if (readSkipAll(userId)) return;
      if (readSeen(userId, nextPageKey)) return;
    }
    setPageKey(nextPageKey);
    setSteps(nextSteps);
    setRun(true);
  }, [userId]);

  const stopTour = useCallback(() => {
    setRun(false);
  }, []);

  const setSkipAll = useCallback((value) => {
    writeSkipAll(userId, value);
  }, [userId]);

  const handleCallback = useCallback((data) => {
    const { status, type, action } = data;

    const finished = [STATUS.FINISHED, STATUS.SKIPPED].includes(status);
    if (finished) {
      if (pageKey) writeSeen(userId, pageKey);
      setRun(false);
      return;
    }

    if (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER) {
      if (pageKey) writeSeen(userId, pageKey);
      setRun(false);
    }
  }, [pageKey, userId]);

  const value = useMemo(() => ({
    startTour,
    stopTour,
    setSkipAll,
    isRunning: run,
  }), [startTour, stopTour, setSkipAll, run]);

  return (
    <TourContext.Provider value={value}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        callback={handleCallback}
        continuous
        showSkipButton
        showProgress={false}
        scrollToFirstStep
        disableScrolling={false}
        disableOverlayClose={false}
        spotlightClicks={false}
        styles={{
          options: {
            primaryColor: "#0f766e",
            textColor: "#1f2937",
            backgroundColor: "#ffffff",
            arrowColor: "#ffffff",
            overlayColor: "rgba(15, 23, 42, 0.55)",
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: 12,
            padding: 16,
            fontSize: 14,
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          },
          tooltipTitle: { fontSize: 16, fontWeight: 600, marginBottom: 6 },
          buttonNext: { borderRadius: 8, padding: "8px 14px", fontSize: 14 },
          buttonBack: { color: "#0f766e", marginRight: 8 },
          buttonSkip: { color: "#6b7280" },
        }}
        locale={{
          back: "Back",
          close: "Close",
          last: "Got it",
          next: "Next",
          skip: "Skip tour",
        }}
      />
    </TourContext.Provider>
  );
}

export function useTourContext() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTourContext must be used inside <TourProvider>");
  }
  return ctx;
}
